import { Fragment } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { ErrorAlert } from '../../components/ErrorAlert';
import CloseIcon from '@mui/icons-material/Close';
import { fmtDateTime } from '../../utils/format';
import type { ReactNode } from 'react';

export type DetailRow<T> = {
  label: string;
  /** Render the value as a string or rich element. Receives the full record. */
  render: (item: T) => ReactNode;
  /** Insert a `<Divider>` above this row. */
  separator?: boolean;
  /** Render the label as a section header above a full-width content
   *  area instead of the default 2-column label/value grid. Use for
   *  embedded charts, tables, or other wide content that shouldn't be
   *  indented by the ~180px label column. */
  fullWidth?: boolean;
};

export type EntityDetailDialogProps<T> = {
  open: boolean;
  title: string;
  isLoading?: boolean;
  error?: { message?: string } | null;
  data?: T | null;
  fields: DetailRow<T>[];
  onClose: () => void;
  /** Override default maxWidth for entities embedding wider content
   *  (e.g. charts). Stage 3 §M-T8.5 — site-controller detail uses 'md'
   *  to fit the heartbeat time-series. Defaults to 'sm'. */
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  /** Optional action buttons rendered in the footer, left of Close.
   *  Detail dialogs are read-only by default (PORTAL_FORM_RULES §5); use
   *  this sparingly, only for a terminal/destructive action that belongs
   *  with the record being viewed (e.g. delete a deprecated release). */
  footerActions?: ReactNode;
};

/**
 * Read-only detail view, rendered as a draggable centred dialog. Layout
 * follows PORTAL_FORM_RULES.md §5: detail-row pattern with explicit
 * separator dividers, audit-trail block at the bottom, em-dash for nulls.
 */
export function EntityDetailDialog<T>(props: EntityDetailDialogProps<T>) {
  const { open, title, isLoading, error, data, fields, onClose, maxWidth = 'sm', footerActions } = props;

  return (
    <Dialog open={open} onClose={onClose} maxWidth={maxWidth} fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
        <Typography variant="h6" component="span">{title}</Typography>
        <IconButton aria-label="Close" onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {isLoading ? (
          <Stack direction="row" spacing={2} alignItems="center">
            <CircularProgress size={20} />
            <Typography>Loading…</Typography>
          </Stack>
        ) : error ? (
          <ErrorAlert>{error.message ?? 'Failed to load.'}</ErrorAlert>
        ) : data ? (
          <Stack spacing={1}>
            {fields.map((field, i) => (
              <Fragment key={`${field.label}-${i}`}>
                {field.separator && <Divider sx={{ my: 1 }} />}
                {field.fullWidth ? (
                  <Stack spacing={1}>
                    <Typography variant="subtitle2" color="text.secondary">
                      {field.label}
                    </Typography>
                    <Box>{field.render(data)}</Box>
                  </Stack>
                ) : (
                  <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '180px 1fr' },
                    rowGap: 0.5,
                    columnGap: 2,
                  }}>
                    <Typography variant="body2" color="text.secondary">{field.label}</Typography>
                    <Typography variant="body2" component="div">{field.render(data)}</Typography>
                  </Box>
                )}
              </Fragment>
            ))}
          </Stack>
        ) : null}
      </DialogContent>
      <DialogActions>
        {footerActions}
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * Standard audit-trail block. Append to every entity's `fields` so detail
 * dialogs all end with the same Created / Updated information.
 */
export function auditTrailFields<T extends {
  createdAt?: string | null;
  createdByName?: string | null;
  updatedAt?: string | null;
  updatedByName?: string | null;
}>(): DetailRow<T>[] {
  return [
    { label: 'Created',     separator: true, render: (r) => fmtDateTime(r.createdAt) },
    { label: 'Created By',  render: (r) => r.createdByName ?? '—' },
    { label: 'Last Updated', render: (r) => fmtDateTime(r.updatedAt) },
    { label: 'Updated By',  render: (r) => r.updatedByName ?? '—' },
  ];
}
